import { apiClient } from './client'
import type { User, PaginatedResponse, UserRole } from '@/types/models'

export interface CreateUserData {
  email: string
  password?: string // Optional: if not provided, backend generates one
  name?: string
  isActive?: boolean
  doctorId?: string
  patientId?: string // For linking to existing patient
  role: UserRole // Agora é obrigatório e único
  clinicIds?: string[]
}

export interface UpdateUserData extends Partial<Omit<CreateUserData, 'email' | 'password'>> {}

export interface UsersQueryParams {
  page?: number
  limit?: number
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
}

export interface UserWithRelations extends User {
  role: UserRole
  clinicIds: string[]
  patientId?: string
  temporaryPassword?: string // Only present when user is created with generated password
}

export interface ResetPasswordResponse {
  success: boolean
  message: string
  temporaryPassword: string
}

export const usersService = {
  // Get all users with pagination
  async getAll(params?: UsersQueryParams): Promise<PaginatedResponse<UserWithRelations>> {
    const response = await apiClient.get('/users', { params })
    return response.data
  },

  // Get current user
  async getMe(): Promise<UserWithRelations> {
    const response = await apiClient.get('/users/me')
    return response.data
  },

  // Search users
  async search(query: string, fields?: string[]): Promise<UserWithRelations[]> {
    const response = await apiClient.get('/users/search', {
      params: { q: query, fields: fields?.join(',') },
    })
    return response.data
  },

  // Get single user by ID
  async getById(id: string): Promise<UserWithRelations> {
    const response = await apiClient.get(`/users/${id}`)
    return response.data
  },

  // Create new user
  async create(data: CreateUserData): Promise<UserWithRelations> {
    const response = await apiClient.post('/users', data)
    return response.data
  },

  // Update user
  async update(id: string, data: UpdateUserData): Promise<UserWithRelations> {
    const response = await apiClient.put(`/users/${id}`, data)
    return response.data
  },

  // Toggle user status
  async toggleStatus(id: string): Promise<UserWithRelations> {
    const response = await apiClient.patch(`/users/${id}/status`)
    return response.data
  },

  // Delete user
  async delete(id: string): Promise<void> {
    await apiClient.delete(`/users/${id}`)
  },

  // Update user role
  async updateRole(id: string, role: UserRole): Promise<UserWithRelations> {
    const response = await apiClient.patch(`/users/${id}/role`, { role })
    return response.data
  },

  // Reset user password
  async resetPassword(id: string): Promise<ResetPasswordResponse> {
    const response = await apiClient.post(`/users/${id}/reset-password`)
    return response.data
  },

  // Add user to clinic
  async addToClinic(userId: string, clinicId: string): Promise<void> {
    await apiClient.post(`/users/${userId}/clinics/${clinicId}`)
  },

  // Remove user from clinic
  async removeFromClinic(userId: string, clinicId: string): Promise<void> {
    await apiClient.delete(`/users/${userId}/clinics/${clinicId}`)
  },
}
